import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges, TemplateRef, ViewChild } from '@angular/core';
import { AbstractControl, FormArray } from '@angular/forms';
import { CdkDragMove, CdkDragStart, moveItemInArray } from '@angular/cdk/drag-drop';
import { Subscription } from 'rxjs';

export interface ReorderPoint {
    x: number;
    y: number;
}

export interface ReorderRect {
    left: number;
    top: number;
    width: number;
}

// Pure pointer→slot mapping on a wrapping grid, clamped to list bounds, so the preview math is spec-able without DOM
export function computeHoverIndex(pointer: ReorderPoint, rect: ReorderRect, cols: number, rowHeight: number, colGap: number, rowGap: number, length: number): number {
    if (length <= 0 || cols <= 0) return 0;

    const colWidth = (rect.width - colGap * (cols - 1)) / cols;
    const colStride = colWidth + colGap;
    const rowStride = rowHeight + rowGap;
    const col = colStride > 0 ? clamp(Math.floor((pointer.x - rect.left) / colStride), 0, cols - 1) : 0;
    const row = rowStride > 0 ? Math.max(Math.floor((pointer.y - rect.top) / rowStride), 0) : 0;

    return Math.min(row * cols + col, length - 1);
}

function clamp(value: number, min: number, max: number): number {
    return Math.min(Math.max(value, min), max);
}

// CDK sorting is disabled; the preview is CSS `order` + badge attrs only — reordering Angular views mid-drag crashes on CDK 10 (dragged element is parked on <body>), so the single moveItemInArray happens on drop
@Component({
    selector: 'pv-dp-reorder-list',
    templateUrl: 'dp-reorder-list.component.html',
    styleUrls: ['dp-reorder-list.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DpReorderListComponent implements OnChanges, OnDestroy {

    @Input() formArray: FormArray;
    @Input() itemTemplate: TemplateRef<any>;
    // Interactive per-item controls render outside the drag handle — CDK 10 starts a drag from any pointerdown inside a handle
    @Input() extrasTemplate: TemplateRef<any>;
    @Input() itemClassFn: (control: AbstractControl) => string | null;
    @Output() remove = new EventEmitter<number>();

    @ViewChild('grid') gridRef: ElementRef<HTMLElement>;

    private dragStartIndex: number | null = null;
    private previewIndex: number | null = null;
    private cols = 1;
    private rowHeight = 0;
    private colGap = 0;
    private rowGap = 0;
    private formArraySub: Subscription;

    constructor(private _changes: ChangeDetectorRef) {}

    ngOnChanges(changes: SimpleChanges) {
        // Re-render on external add/remove — the parent mutates the FormArray directly
        if (changes.formArray) {
            if (this.formArraySub) this.formArraySub.unsubscribe();
            if (this.formArray) this.formArraySub = this.formArray.valueChanges.subscribe(() => this._changes.markForCheck());
        }
    }

    ngOnDestroy() {
        if (this.formArraySub) this.formArraySub.unsubscribe();
    }

    // trackBy control identity — re-rendering the dragged element mid-drag destroys its DOM node and breaks the CDK drag session
    trackByControl = (_index: number, control: AbstractControl) => control;

    onDragStarted(index: number, event: CdkDragStart) {
        const style = getComputedStyle(this.gridRef.nativeElement);
        this.dragStartIndex = index;
        this.previewIndex = index;
        this.cols = style.gridTemplateColumns.split(' ').length;
        this.colGap = parseFloat(style.columnGap) || 0;
        this.rowGap = parseFloat(style.rowGap) || 0;
        this.rowHeight = event.source.getPlaceholderElement().offsetHeight;
    }

    onDragMoved(event: CdkDragMove) {
        if (!this.formArray) return;

        const rect = this.gridRef.nativeElement.getBoundingClientRect();
        const hover = computeHoverIndex(event.pointerPosition, rect, this.cols, this.rowHeight, this.colGap, this.rowGap, this.formArray.length);
        if (hover === this.previewIndex) return;

        this.previewIndex = hover;
        this.applyPreview(event.source.getPlaceholderElement(), hover);
    }

    onDropped() {
        this.gridItems().forEach(el => el.style.order = '');

        // detectChanges (not markForCheck): the DOM must reorder synchronously in this zone task, before anything paints
        if (this.formArray && this.dragStartIndex !== null && this.previewIndex !== null && this.previewIndex !== this.dragStartIndex) {
            moveItemInArray(this.formArray.controls, this.dragStartIndex, this.previewIndex);
            this.formArray.updateValueAndValidity();
            this._changes.detectChanges();
        }

        this.dragStartIndex = this.previewIndex = null;
    }

    move(from: number, to: number) {
        if (!this.formArray || to < 0 || to >= this.formArray.length) return;

        moveItemInArray(this.formArray.controls, from, to);
        this.formArray.updateValueAndValidity();
        this._changes.markForCheck();
    }

    // Insert-and-shift: the placeholder takes the hovered order, the rest shift around it; DOM source order never changes
    private applyPreview(placeholder: HTMLElement, hover: number) {
        const others = this.gridItems().filter(el => el !== placeholder);

        others.forEach((el, k) => this.setSlot(el, k < hover ? k : k + 1));
        this.setSlot(placeholder, hover);
    }

    private setSlot(el: HTMLElement, pos: number) {
        el.style.order = String(pos);

        const badge = el.querySelector('.dp-num');
        if (badge) badge.setAttribute('data-pos', String(pos + 1));
    }

    private gridItems(): HTMLElement[] {
        return Array.from(this.gridRef.nativeElement.querySelectorAll('.dp-item')) as HTMLElement[];
    }
}
